import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient } from "@/lib/api";
import { ClipboardList, Send, Hammer, Hourglass, CheckCircle2, AlertTriangle, Lock } from "lucide-react";

const COLS = [
  { key: "a_chiffrer", label: "À chiffrer", icon: ClipboardList, accent: "border-t-zinc-400" },
  { key: "devis_envoyes", label: "Devis envoyés", icon: Send, accent: "border-t-sky-500" },
  { key: "en_cours", label: "En cours", icon: Hammer, accent: "border-t-[#FF4D00]" },
  { key: "a_valider", label: "À valider", icon: Hourglass, accent: "border-t-amber-500" },
  { key: "termines", label: "Terminés", icon: CheckCircle2, accent: "border-t-emerald-600" },
];

const eur = (v) => (v || v === 0 ? `${Number(v).toLocaleString("fr-FR")} €` : null);

export default function ArtisanOrganizer() {
  const [data, setData] = useState(null);

  useEffect(() => {
    apiClient.get("/artisans/me/organizer")
      .then(({ data }) => setData(data))
      .catch(() => setData({}));
  }, []);

  if (!data) return null;

  return (
    <div className="mb-8 border-2 border-[#0A0A0B] bg-white" data-testid="artisan-organizer">
      <div className="border-b-2 border-[#0A0A0B] px-6 py-4 flex items-center gap-2">
        <ClipboardList className="w-5 h-5 text-[#FF4D00]" />
        <div className="display text-2xl">Organiseur de chantiers</div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-0 divide-y-2 lg:divide-y-0 lg:divide-x-2 divide-zinc-100">
        {COLS.map(({ key, label, icon: Icon, accent }) => {
          const items = data[key] || [];
          return (
            <div key={key} className={`border-t-4 ${accent}`} data-testid={`organizer-col-${key}`}>
              <div className="px-4 py-3 flex items-center justify-between">
                <div className="mono text-[10px] uppercase tracking-widest text-zinc-600 flex items-center gap-1.5">
                  <Icon className="w-3.5 h-3.5" /> {label}
                </div>
                <span className="display text-lg">{items.length}</span>
              </div>
              <div className="px-3 pb-3 space-y-2 max-h-64 overflow-auto">
                {items.length === 0 && (
                  <div className="text-[11px] text-zinc-400 px-1 pb-2">—</div>
                )}
                {items.map((m) => (
                  <Link
                    key={m.id}
                    to={`/missions/${m.id}`}
                    className="block border border-zinc-200 hover:border-[#FF4D00] p-2.5 transition-colors"
                    data-testid={`organizer-item-${m.id}`}
                  >
                    <div className="text-xs font-bold truncate">{m.title}</div>
                    <div className="text-[10px] text-zinc-500 truncate">
                      {m.city}
                      {m.amount
                        ? ` · ${eur(m.amount)}`
                        : m.budget_min
                          ? ` · ${eur(m.budget_min)}–${eur(m.budget_max)}`
                          : ""}
                    </div>
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {m.deadline && (
                        <span className={`mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 border ${m.late_days > 0 ? "bg-red-50 text-red-700 border-red-300" : "bg-zinc-50 text-zinc-600 border-zinc-200"}`}>
                          {m.late_days > 0 ? (
                            <span className="inline-flex items-center gap-0.5"><AlertTriangle className="w-2.5 h-2.5" /> Retard J+{m.late_days}</span>
                          ) : (
                            `Échéance ${m.deadline.split("-").reverse().join("/")}`
                          )}
                        </span>
                      )}
                      {m.balance_pending && (key === "en_cours" || key === "a_valider") && (
                        <span className="mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 border bg-orange-50 text-[#FF4D00] border-orange-300 inline-flex items-center gap-0.5">
                          <Lock className="w-2.5 h-2.5" /> Solde en attente
                        </span>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
